/**
 * LA document type names and codes for proposal uploads.
 * Source: business mapping (document category + LA code).
 *
 * Extend `*_BY_NORMALIZED_LABEL` when API `documentName` values differ — keys are
 * lowercase, trimmed, single-spaced (see `normalizeDocumentLabel`).
 */

export const PR_DOCUMENT_TYPE = {
  INCOME_PROOF: "PR_IncomeProof",
  BANK_ACCOUNT_PROOF: "PR_BankAccountProof",
  IDENTIFICATION_PROOF: "PR_IndentificationProof",
  ADDRESS_PROOF: "PR_AddressProof",
  AGE_PROOF: "PR_AgeProof",
  PHOTOGRAPH: "PR_PHOTO",
  FATCA_DECLARATION: "PR_FATCA",
};

/** Income proof: form uses plain strings (INCOME_PROOF_OPTIONS). */
export const INCOME_PROOF_PR_BY_LABEL = {
  "recent appointment letter/salary revision letter": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
  "last 3 month's salary slips": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
  "last 6 months bank statements reflecting salary credit at regular periodic interval": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "BANK STM",
  },
  "income tax returns (itr) for last 2 years along with computation of income": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
  "lates form 16 / form 16a": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
  "form 26as": {
    documentType: PR_DOCUMENT_TYPE.INCOME_PROOF,
    documentTypeCode: "OTHERS",
  },
};

/** Photograph: PHOTOGRAPH_OPTIONS — both map to LA_PHOTO. */
export const PHOTOGRAPH_PR_BY_LABEL = {
  "passport photo": {
    documentType: PR_DOCUMENT_TYPE.PHOTOGRAPH,
    documentTypeCode: "PR_PHOTO",
  },
  selfie: {
    documentType: PR_DOCUMENT_TYPE.PHOTOGRAPH,
    documentTypeCode: "PR_PHOTO",
  },
};

/**
 * Address proof: keys match API `documentName` after normalization.
 * Add or adjust keys if masters return different wording.
 */
export const ADDRESS_PROOF_PR_BY_LABEL = {
  "driving license": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  "driving licence": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  "telephone bill": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "bank statement": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK STM",
  },
  "electricity bill": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "ELCT BIL",
  },
  "lease agreement": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "LEASE AG",
  },
  passport: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "PASSPORT",
  },
  "voter id": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "voter's id": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "bank certificate": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK CER",
  },
  popass: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "POPASS",
  },
  "post office passport": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "POPASS",
  },
  aadhar: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "AADHAR",
  },
  aadhaar: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "AADHAR",
  },
  bihar: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BIHAR",
  },
  domicile: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DOMICILE",
  },
  "domicile certificate": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "DOMICILE",
  },
  certward: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "CERTWARD",
  },
  "insurance policy": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "INSUPOLI",
  },
  "letter from sem/pa*": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "SEM/GO/P",
  },
  "consumer gas connection card/ book/ pipe gas bill (2 months)": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "GASCARD",
  },
  pension: {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "PENSIONP",
  },
  "pension payment": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "PENSIONP",
  },
  "pan card": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "SI",
  },
  "telephone/ mobile bill (2 months)": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "telephone / mobile bill (2 months)": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "telephone and mobile bill (2 months)": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "mobile bill": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "TEL BILL",
  },
  "bank account statement (3 months)": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK STM",
  },
  "banker's certification": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "BANK CER",
  },
  "certificate by village extension officer (veo)/ village head/ equivalent rank officer": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "VEOCERT",
  },
  "post office savings pass book": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "POP ASS",
  },
  "certificate from ward officer/ equivalent rank officer maintaining election roll certifying address of applicant": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "CERTWARD",
  },
  "letter of allotment of accommodation from employer issued by state or central government departments, statutory or regulatory bodies, public sector undertakings, scheduled commercial banks, financial institutions and listed companies": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "OTHERS",
  },
  "kra kyc records": {
    documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF,
    documentTypeCode: "OTHERS",
  },
};

/** Identification proof — keys normalized from `documentName`. */
export const IDENTIFICATION_PROOF_PR_BY_LABEL = {
  passport: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PASSPORT",
  },
  "pan card": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PAN CARD",
  },
  pan: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PAN CARD",
  },
  photocer: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PHOTOCER",
  },
  "photo certificate": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "PHOTOCER",
  },
  "bank certificate": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "BANK CER",
  },
  "voter id": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "voter ic": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "nrega card": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NREGACRD",
  },
  nregacrd: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NREGACRD",
  },
  "card issued by national rural employment guarantee act (nrega)": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NREGACRD",
  },
  bihar: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "BIHAR",
  },
  nebihar: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NEBIHAR",
  },
  "ne bihar": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "NEBIHAR",
  },
  ice: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "ICE",
  },
  "driving license": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "DRVR LIC",
  },
  "driving licence": {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "DRVR LIC",
  },
  aadhar: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "AADHAR",
  },
  aadhaar: {
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
    documentTypeCode: "AADHAR",
  },
};

/** Bank Account proof — keys normalized from `documentName`. */
export const BANK_ACCOUNT_PROOF_PR_BY_LABEL = {
  // The Primary/Secondary Annuitant "Bank Account Proof" field (Annuitant
  // Tab) offers the same three choices as the Proposer's own bank-details
  // upload (Cancelled Cheque/Passbook/Bank Statement) — those raw labels
  // weren't in this map, so resolveSlotLaPayload always returned null for
  // them and blocked the upload. "BANK STM" matches this category's own
  // FALLBACK_PR_BY_SLOT default below.
  "cancelled cheque": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BANK STM",
  },
  passbook: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BANK STM",
  },
  "bank statement": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BANK STM",
  },
  passport: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "PASSPORT",
  },
  "passport-valid": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "VISADOC",
  },
  "identity card issued in north east & bihar by the state government": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "OTHERS",
  },
  "pan card": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "PAN CARD",
  },
  pan: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "PAN CARD",
  },
  "photograph certified by an insurance company employee (ice)": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "CERT PIC",
  },
  "photo certificate": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "PHOTOCER",
  },
  "bank certificate": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BANK CER",
  },
  "voter id": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "voter ic": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "VOTER IC",
  },
  "nrega card": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "NREGACRD",
  },
  nregacrd: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "NREGACRD",
  },
  bihar: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BIHAR",
  },
  nebihar: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "NEBIHAR",
  },
  "ne bihar": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "NEBIHAR",
  },
  ice: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "ICE",
  },
  "driving license": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  "driving licence": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "DRIV LCN",
  },
  aadhar: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "AADHAR",
  },
  aadhaar: {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "AADHAR",
  },
  "employer certification": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "CERTEMP",
  },
  "bank certification": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "BANK CER",
  },
  "defence id card": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "OTHERS",
  },
  "Employee ID Card": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "OTHERS",
  },
  "voters identity card": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "OTHERS",
  },
  "card issued by national rural employment guarantee act (nrega)": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "NREGACRD",
  },
  "kra kyc records": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "OTHERS",
  },
  "*sem/go/pa verification": {
    documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF,
    documentTypeCode: "PBL CERT",
  },
};

/** Age proof — extend when master lists full document names. */
export const AGE_PROOF_PR_BY_LABEL = {
  "driving license": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "DRIV LCN" },
  "driving licence": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "DRIV LCN" },
  "aadhar card with date of birth": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "NU" },
  "army identity card": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "SA" },
  "aadhar": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "AADHAR" },
  "army card": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "ARMYCARD" },
  "pan": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "PAN CARD" },
  "pan card": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "PAN CARD" },
  "pass port": { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "PASSPORT" },
  passport: { documentType: PR_DOCUMENT_TYPE.AGE_PROOF, documentTypeCode: "PASSPORT" },
};

/**
 * Last-resort LA payload by upload slot when selection context is unavailable
 * (e.g. review screen before selections are persisted).
 */
export const FALLBACK_PR_BY_SLOT = {
  photograph: { documentTypeCode: "PR_PHOTO", documentType: PR_DOCUMENT_TYPE.PHOTOGRAPH },
  ageProof: { documentTypeCode: "NU", documentType: PR_DOCUMENT_TYPE.AGE_PROOF },
  identificationProof: {
    documentTypeCode: "PAN CARD",
    documentType: PR_DOCUMENT_TYPE.IDENTIFICATION_PROOF,
  },
  addressProof: { documentTypeCode: "BANK STM", documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF },
  addressProofPermanent: { documentTypeCode: "BANK STM", documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF },
  addressProofCommunication: { documentTypeCode: "BANK STM", documentType: PR_DOCUMENT_TYPE.ADDRESS_PROOF },
  incomeProof: { documentTypeCode: "BNK STMT", documentType: PR_DOCUMENT_TYPE.INCOME_PROOF },
  bankAccountProof: { documentTypeCode: "BANK STM", documentType: PR_DOCUMENT_TYPE.BANK_ACCOUNT_PROOF},
};

const CACHE_KEYS = {
  addressProof: "addressdocumentproof",
  ageProof: "agedocumentproof",
  identificationProof: "identificationdocumentproof",
  incomeProof: "incomeproofs",
};

export function normalizeDocumentLabel(value) {
  if (value == null || String(value).trim() === "") return "";
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/");
}

function lookupByNormalizedMap(map, label) {
  const key = normalizeDocumentLabel(label);
  if (!key) return null;
  // Map keys are hand-written and not guaranteed to match the normalized
  // slash-spacing (e.g. "officer/ equivalent" vs API's "officer/equivalent"),
  // so normalize each key the same way before comparing.
  for (const [k, v] of Object.entries(map)) {
    const normalizedKey = normalizeDocumentLabel(k);
    if (key === normalizedKey) return v;
  }
  for (const [k, v] of Object.entries(map)) {
    const normalizedKey = normalizeDocumentLabel(k);
    if (key.includes(normalizedKey) || normalizedKey.includes(key)) return v;
  }
  return null;
}

/**
 * @param {'incomeProof'|'addressProof'|'ageProof'|'identificationProof'|'photograph'} category
 * @param {string} selectionLabel — display name or form string
 * @returns {{ documentType: string, documentTypeCode: string } | null}
 */
export function getLaPayloadForCategory(category, selectionLabel) {
  const label = selectionLabel;
  switch (category) {
    case "photograph":
      return lookupByNormalizedMap(PHOTOGRAPH_PR_BY_LABEL, label);
    case "incomeProof":
      return lookupByNormalizedMap(INCOME_PROOF_PR_BY_LABEL, label);
    case "addressProof":
      return lookupByNormalizedMap(ADDRESS_PROOF_PR_BY_LABEL, label);
    case "ageProof":
      return lookupByNormalizedMap(AGE_PROOF_PR_BY_LABEL, label);
    case "identificationProof":
      return lookupByNormalizedMap(IDENTIFICATION_PROOF_PR_BY_LABEL, label);
    case "bankAccountProof":
      return lookupByNormalizedMap(BANK_ACCOUNT_PROOF_PR_BY_LABEL, label);
    case "fatcaDeclaration":
      return { documentType: PR_DOCUMENT_TYPE.FATCA_DECLARATION, documentTypeCode: "FATCA" };
    default:
      return null;
  }
}

function masterDocumentName(field, formValue, apiCache) {
  const cacheKey = CACHE_KEYS[field];
  if (!cacheKey || formValue == null || formValue === "") return "";
  const list = apiCache[cacheKey]?.data || [];
  const row = list.find(
    (d) => String(d.documentID) === String(formValue),
  );
  return row?.documentName || "";
}

const SLOT_CATEGORY = {
  photograph: "photograph",
  addressProof: "addressProof",
  addressProofPermanent: "addressProof",
  addressProofCommunication: "addressProof",
  ageProof: "ageProof",
  incomeProof: "incomeProof",
  identificationProof: "identificationProof",
  bankAccountProof: "bankAccountProof",
  fatcaDeclaration: "fatcaDeclaration",
};

const getDocumentPrefix = (personType) => {
  switch (personType) {
    case "primary":
      return "LA";
    case "secondary":
      return "LA2";
    case "proposer":
    default:
      return "PR";
  }
};

const applyPrefix = (documentType, prefix) => {
  if (!documentType) return documentType;

  return documentType.replace(/^PR_/, `${prefix}_`);
};

const getFormDataPrefix = (personType) => {
  switch (personType) {
    case "primary":
      return "pa";
    case "secondary":
      return "sa";
    case "proposer":
    default:
      return "";
  }
};

const getFieldValue = (formData, field, personType) => {
  const prefix = getFormDataPrefix(personType);

  const key = prefix
    ? `${prefix}${field.charAt(0).toUpperCase()}${field.slice(1)}`
    : field;

  return formData?.[key];
};

/**
 * Resolves LA `documentType` + `documentTypeCode` for an upload slot from
 * proposal form data and journey API cache.
 *
 * @param {string} slotId — e.g. addressProof, idProof
 * @param {{ formData: object, apiCache: object }} context
 */
export function resolveSlotLaPayload(slotId, context) {
  const { formData, apiCache = {}, personType } = context;

  if (slotId === "fatcaDeclaration") {
    return {
      documentType: PR_DOCUMENT_TYPE.FATCA_DECLARATION,
      documentTypeCode: "FATCA",
    };
  }

  const category = SLOT_CATEGORY[slotId];
  if (!category) return null;

  let selectionLabel = "";
  if (category === "photograph") {
    selectionLabel = getFieldValue(formData, "photograph", personType);
  } else if (category === "incomeProof") {
    const selectedId = getFieldValue(formData, "incomeProof", personType);
    // Form stores documentID from incomeproofs master; map to documentName
    // for INCOME_PROOF_PR_BY_LABEL lookup (same pattern as address/age/id).
    selectionLabel =
      masterDocumentName("incomeProof", selectedId, apiCache) || selectedId;
  } else if (category === "bankAccountProof") {
    const selectedId = getFieldValue(formData, "bankAccountProof", personType);
    selectionLabel = masterDocumentName("identificationProof", selectedId, apiCache) || selectedId;
  } else if (category === "addressProof") {
    const selectedId =
      getFieldValue(formData, "addressProof", personType) ||
      getFieldValue(formData, "addressProofPermanent", personType) ||
      getFieldValue(formData, "addressProofCommunication", personType);

    selectionLabel =
      masterDocumentName("addressProof", selectedId, apiCache) ||
      selectedId;
  } else if (category === "ageProof") {
    const selectedId = getFieldValue(formData, "ageProof", personType);
    selectionLabel = masterDocumentName("ageProof", selectedId, apiCache) || selectedId;
  } else if (category === "identificationProof") {
    const selectedId = getFieldValue(formData, "identificationProof", personType);

    selectionLabel =
      masterDocumentName("identificationProof", selectedId, apiCache) ||
      selectedId;
  }

  const laPayload = getLaPayloadForCategory(category, selectionLabel);
  if (!laPayload) return null;

  const prefix = getDocumentPrefix(personType);

  return {
    ...laPayload,
    documentType: applyPrefix(laPayload.documentType, prefix),
  };
}